/**
 * Sandboxes v2 applications: one full-width list with a header per application
 * (delivery and head) and a row per environment showing readiness and a Claim
 * button that claims the head, plus a link to the application's Releases page,
 * which owns version history.
 *
 * @module features/sandboxes/components/v2/ApplicationsSection
 */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Flex, Heading, ListCard, ListCardRow, Text } from '@chatsdet/ui';
import { ArrowRight, GitBranch, Package, Plus } from '@phosphor-icons/react';
import { useClaimFeedback, useClaimSandboxV2 } from '../../../../hooks/useClaimSandboxV2';
import type { SandboxV2Application } from '../../hooks/useSandboxesV2';
import { formatRelative } from '../overview/time-format';
import { ReadinessChip, versionName } from './sandboxV2Display';

function ApplicationGroup({ application }: { application: SandboxV2Application }) {
  const claim = useClaimSandboxV2();
  const feedback = useClaimFeedback(application.name);
  const isUpload = application.delivery === 'upload';
  const [claimingEnv, setClaimingEnv] = React.useState<string | null>(null);

  const handleClaim = (environment: string) => {
    setClaimingEnv(environment);
    claim.mutate(
      { applicationId: application.id, environment },
      {
        ...feedback,
        onSettled: () => setClaimingEnv(null),
      }
    );
  };

  const latest = application.latestVersion;

  return (
    <div data-testid="sandbox-v2-application">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-muted/40 px-4 py-3">
        <Flex direction="column" gap="xs" className="min-w-0">
          <Text weight="semibold" className="truncate">
            {application.name}
          </Text>
          <Flex align="center" gap="xs" className="text-muted-foreground">
            {isUpload ? <Package size={12} /> : <GitBranch size={12} />}
            <Text size="xs" className={`truncate text-muted-foreground${isUpload ? '' : ' font-mono'}`}>
              {isUpload ? 'Pushed image' : (application.repo ?? 'GitHub')}
            </Text>
            <Text size="xs" className="text-muted-foreground">
              · Head{' '}
              {latest ? (
                <>
                  <span className="font-mono">{versionName(latest.tags, latest.version)}</span>
                  {isUpload ? ` · ${formatRelative(latest.at)}` : ''}
                </>
              ) : (
                'No version yet'
              )}
            </Text>
          </Flex>
        </Flex>
        <Link
          to="/releases/$propertyId"
          params={{ propertyId: application.id }}
          className="flex items-center gap-1 text-sm text-primary hover:underline"
        >
          Releases
          <ArrowRight size={14} />
        </Link>
      </div>
      {application.environments.length === 0 ? (
        <div className="border-t border-border px-4 py-3">
          <Text size="sm" color="muted">
            No sandbox environments yet.
          </Text>
        </div>
      ) : (
        application.environments.map((env, index) => (
          <ListCardRow
            key={env.id}
            index={index}
            title={env.name}
            badges={<ReadinessChip status={env.readiness.status} error={env.readiness.error} />}
            actions={
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus size={14} />}
                onPress={() => handleClaim(env.environment)}
                isDisabled={claim.isPending}
              >
                {claim.isPending && claimingEnv === env.environment ? 'Claiming…' : 'Claim'}
              </Button>
            }
            // Rows sit inside the per-application wrapper, out of ListCard's divide-y reach.
            className="border-t border-border"
          />
        ))
      )}
    </div>
  );
}

export function ApplicationsSection({ applications }: { applications: SandboxV2Application[] }) {
  return (
    <section className="flex flex-col gap-3" aria-labelledby="sandbox-v2-applications">
      <Heading level={3} id="sandbox-v2-applications">
        Applications
      </Heading>
      <ListCard>
        {applications.map((application) => (
          <ApplicationGroup key={application.id} application={application} />
        ))}
      </ListCard>
    </section>
  );
}
